import { useEffect, useRef, useState } from "react";  //M10-12020
import { CheckCircle, Circle } from "lucide-react"; //M10-15262
import moment from "moment";
import { get120DaysAgoDate, switchString } from "../../utils/utils";
import { Autocomplete, Button, DatePicker, Input, Modal, Spinner, Toggle } from "src/components";
import downArrow from "src/assets/down-arrow.svg";
import editPen from "src/assets/edit-pen.svg";
import { downloadInvoice, editInvoice, getInvoiceChangeValidationV1, getInvoiceDetails, getPoRefNoListing, postInvoiceRemark, renameInvoiceNumber, uploadInvoice } from "src/services/invoice-api"; //M10-11703
import { generateUniqueID } from "src/constants/functions";
import InvoiceReject from "./invoice-reject";
import InvoiceReassign from "../invoice-master/InvoiceReassign";
import { useNavigate } from "react-router-dom";
import useIsMounted from "src/hooks/useIsMounted";  //M10-15110
import useSingleFetchAutocomplete from "src/hooks/useSingleFetchAutocomplete";   // M10-15867
import Contact from "src/assets/Contact.svg";
import { toast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import { invoiceActions } from "src/context/invoice-slice";
import { useDispatch, useSelector } from "react-redux"; //M10-11703
import { counterActions } from "src/context/counter-slice";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import useDebounceLeading from "src/hooks/useDebounceLeading";
import EscalateToMaster from "./InvoiceEntryCards/EscalateToMaster";
import SelectCounterModal from "./InvoiceEntryCards/SelectCounterModal";
import VerifyPoRefModal, { usePoRefVerification } from "./InvoiceEntryCards/VerifyPoRefModal"; //M10-16162
import { useCounterSelection } from "src/hooks/useCounterSelection";
import { DateTimeInput } from "src/features/fields/components/DateTimeInput";
import { validateInvoiceNumber } from "./invoice-constants"; //M10-11021
//M10-11703
import crossIcon from "src/assets/cross.svg";
import { RootState } from "src/context/store";
//M10-11703
import usePermission from "src/hooks/usePermission";
import { getFinancialYear, isValidKeyboardInput, getLiteralNowIso, isEwayBillPending, EWAY_BILL_PENDING_MESSAGE } from "../invoice-master/invoice-helper"; //M10-12861, M10-16202

interface InvoiceDetailCardProps {
  status: string;
  data: any;
  checkDataType: boolean;
  viewDetails?: boolean;
  isExpandable?: boolean;
  isExpandedInitial?: boolean;
  isCompleted?: boolean;
  isEntry?: boolean;
  index?: number;
  invoiceData?: any;
  showHeader?: boolean;
  fetchInvoiceDetails?: any;
  tab: number;
  isEwayExempt?: boolean;
  onEwayBillPending?: () => void; //M10-16202: lets the screen open its E-Way Bill popup
  isMapped?: boolean; //M10-11117
  isFuidRejectionReview?: boolean;
}

const InvoiceDetailCard = ({
  data,
  checkDataType = true,
  status,
  isCompleted = false,
  invoiceData,
  showHeader = true,
  tab,
  onEwayBillPending,
  isMapped = true,  //M10-11117
  isFuidRejectionReview = false,
}: InvoiceDetailCardProps) => {
  const [invoiceNumber, setInvoiceNumber] = useState<string>(data?.["Invoice no."]);
  const [invoiceDate, setInvoiceDate] = useState<string>(data?.["Invoice date"]);
  // Mandatory only for KAM (module_code 4) invoices; must always be >= Invoice Date.
  // Defaults to Invoice Date (when one exists) but the user can freely change the date and/or time from there.
  const [goodsDeliveredDate, setGoodsDeliveredDate] = useState<string>(invoiceData?.invoice?.goods_delivered_date || invoiceData?.invoice?.new_invoice_date);
  // Available: user picks their own date, editable. Not Available: mirrors Invoice Date, read-only.
  // Hydrated from the saved value when one exists; null (no default) only for a brand-new invoice
  // that has never had this field set — the user must then explicitly pick one before submitting.
  const [goodsDeliveredAvailability, setGoodsDeliveredAvailability] = useState<string | null>(
    invoiceData?.invoice?.is_goods_delivered_date_available === true
      ? "Available"
      : invoiceData?.invoice?.is_goods_delivered_date_available === false
      ? "Not Available"
      : null
  );
  const [qrIRN, setQrIRN] = useState<any>(data?.["QR/IRN"]);
  const [irnNumber, setIrnNumber] = useState<string>(data?.["Irn Number"]);
  const [gstStatus, setGstStatus] = useState<string>(data?.["GST Applicability"]); //M10-11546
  const [dlValidated, setdlValidated] = useState<string>(data?.["DL Present"]);
  const [mciValid, setMciValid] = useState<string>(data?.["MCI Validated"]);
  const [isExpanded, setIsExpanded] = useState(tab !== 1);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [escalateToMasterOpen, setEscalateToMasterOpen] = useState(false);
  // PUC (module_code 3) invoices: MCI Validated, Stamp & Sign Status and E-Inv are locked; the latter two default to "No"
  const isPucModule = Number(invoiceData?.module_code) === 3;
  const [StampSeal, setStampSeal] = useState<any>(isPucModule ? "No" : data?.["Stamp & Sign Status"]);//M10-13053
  const [category1, setCategory1] = useState<any>(isPucModule ? "No" : data?.["E-Inv"]);//M10-13053
  //M10-11703
  const [poReason, setPoReason] = useState<string>('');
  const [poProportionate, setPoProportionate] = useState<string>(data?.["Po Proportionate Exempt"]);
  const [isPoProportionate, setIsPoProportionate] = useState(false);
  const { idTokenDetails } = useSelector((state: RootState) => state.auth);
  //M10-11703
  //M10-15732
  const [poRefNumber, setPoRefNumber] = useState<string>(invoiceData?.poid_ref_number || "");
  const [isPoRefMatched, setIsPoRefMatched] = useState<boolean>(invoiceData?.is_poid_ref_matched === true);
  const [selectedStockistPoAttachmentId, setSelectedStockistPoAttachmentId] = useState<number | null>(
    invoiceData?.stockist_po_attachment_id ?? null
  );
  // M10-15818: order_status_id of the PO picked from the PO Ref No dropdown — 3 means the linked
  // order is already fully invoiced, so submitting the header against it must be blocked.
  const [selectedPoRefOrderStatusId, setSelectedPoRefOrderStatusId] = useState<number | null>(null);
  //M10-15732
  const poRefVerification = usePoRefVerification(poRefNumber); //M10-16162
  //M10-12755
  const [isEditingInvoiceNumber, setIsEditingDate] = useState(false);
  const [isDuplicateNumber, setDuplicateNumber] = useState('');
  //M10-12755
  const [isErrorInvoice, setIsErrorInvoice] = useState("");//M10-12999
  //M10-11086
  // const [IRNValidation, setIRNValidation] = useState<boolean>(
  //   invoiceData?.invoice?.e_inv_ref_type === "IRN" ||
  //   invoiceData?.invoice?.e_inv_ref_type === "BOTH"
  // );
  // const [IRNExempt, setIRNExempt] = useState<any>(invoiceData?.invoice?.irn_exempt);
  const [tempDateChange, settempDateChange] = useState<boolean>(false); //M10-12020
  //M10-11086

  //M10-15262
  // Verification is tracked in redux (keyed by invoice_id) instead of local state so it
  // survives tab switches and screen navigation while the user works through the same invoice.
  const isYetToProcess = invoiceData?.fileStatusHdr?.fuid_status_id === 1;
  const invoiceVerificationId = invoiceData?.invoice?.invoice_id ?? null;
  const { invoiceVerification } = useSelector((state: RootState) => state.invoice);
  const isCurrentInvoiceVerification = invoiceVerification?.invoiceId === invoiceVerificationId;
  const isInvoiceDownloaded = isCurrentInvoiceVerification && invoiceVerification.isDownloaded;
  const isInvoiceNumberVerified = isCurrentInvoiceVerification && invoiceVerification.isNumberVerified;
  const isInvoiceDateVerified = isCurrentInvoiceVerification && invoiceVerification.isDateVerified;
  const setIsInvoiceDownloaded = (value: boolean) =>
    dispatch(invoiceActions.setInvoiceVerification({ invoiceId: invoiceVerificationId, isDownloaded: value }));
  const setIsInvoiceNumberVerified = (value: boolean) =>
    dispatch(invoiceActions.setInvoiceVerification({ invoiceId: invoiceVerificationId, isNumberVerified: value }));
  const setIsInvoiceDateVerified = (value: boolean) =>
    dispatch(invoiceActions.setInvoiceVerification({ invoiceId: invoiceVerificationId, isDateVerified: value }));
  // Re-entering the number/date after a prior verification only needs re-verifying
  // that same field, not a fresh download and not re-verifying the other field.
  const relockNumberVerification = () => {
    dispatch(invoiceActions.setInvoiceVerification({
      invoiceId: invoiceVerificationId,
      isNumberVerified: false,
    }));
  };
  const relockDateVerification = () => {
    dispatch(invoiceActions.setInvoiceVerification({
      invoiceId: invoiceVerificationId,
      isDateVerified: false,
    }));
  };
  //M10-15262

  const company_code = invoiceData?.company_code;

  // M10-15867: getPoRefNoListing has no real pagination and always returns the same full list —
  // fetch it once per counter/location/date/company and skip re-fetching on every
  // keystroke/scroll/dropdown-open the Autocomplete triggers.
  const fetchPoRefList = useSingleFetchAutocomplete(
    () =>
      getPoRefNoListing({
        counter_code: invoiceData?.counter_code,
        location_code: invoiceData?.location_code,
        invoice_date: invoiceDate,
        company_code,
      }),
    [invoiceData?.counter_code, invoiceData?.location_code, invoiceDate, company_code]
  );
  //M10-15732: shared by the PO Ref No dropdown and the M10-16162 verify popup's suggested matches
  const handlePoRefChange = (value: any) => {
    const isListedPo = !!value && !value.isCustom;
    setPoRefNumber(value?.poid_ref_number || "");
    setIsPoRefMatched(isListedPo);
    setSelectedStockistPoAttachmentId(isListedPo ? value.stockist_po_attachment_id ?? null : null);
    setSelectedPoRefOrderStatusId(isListedPo ? value.order_status_id ?? null : null); //M10-15818
  };
  const {
    isNmvYetToProcess,
    allowCustomerNameEntry,
    counterList,
    selectedCounter,
    isCounterModalOpen,
    tempSelectedCounter,
    setTempSelectedCounter,
    openCounterModal,
    closeCounterModal,
    confirmCounterSelection,
    counterPayloadFields,
  } = useCounterSelection({
    companyCode: company_code,
    stockistId: invoiceData?.stockist_id,
    uploadType: invoiceData?.upload_type,
    fuidStatusId: invoiceData?.fileStatusHdr?.fuid_status_id,
    moduleCode: invoiceData?.module_code,
    existingCounterCode: invoiceData?.counter_code,
    existingCounterName: invoiceData?.counter_name,
    existingCustomerName: invoiceData?.customer_name,
  });
  // Whether the Counter row currently represents a walk-in customer entry rather than a real counter
  const isCustomerNameEntry = Boolean(selectedCounter?.isCustomerEntry || (!selectedCounter && invoiceData?.customer_name));

  const catTwoAndThree =
    invoiceData?.einvack_type_code === "Upload E-Inv Aknowledgement Copy separately" ||
    invoiceData?.einvack_type_code === "Periodic Bulk Upload of E-Invoice";

  const { withEventCheck } = useEventStatus();
  const navigate = useNavigate();
  const isMountedRef = useIsMounted();  //M10-15110

  const [modalStates, setModalStates] = useState({
    reject: false,
    reassign: false,
    hold: false,
  });

  const { hasPermission: invoiceNoRenamePermission, loading: isRenamePermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.RENAMEINVOICENUMBER.module,
    action: fineGrainedPermissionsKeys.INVOICE.RENAMEINVOICENUMBER.action,
  });
  const { hasPermission: hasPermissionReject } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.REJECT.module,
    action: fineGrainedPermissionsKeys.INVOICE.REJECT.action,
  });
  const { hasPermission: hasPermissionInvoicePreDate } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.INVOICEPREDATE.module,
    action: fineGrainedPermissionsKeys.INVOICE.INVOICEPREDATE.action,
  });
  const { hasPermission: hasPermissionEditCounter } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.EDITCOUNTER.module,
    action: fineGrainedPermissionsKeys.INVOICE.EDITCOUNTER.action,
  });

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleInputChange = (e: any) => {
    const inputValue = e.target.value;
    //M10-12999
    let errorMessage = "";

    if (/[-_]\s?1$/.test(inputValue)) {
      errorMessage = "Invoice number cannot end with -1 or - 1";
    }

    if (inputValue.length > 35) {
      errorMessage = "Invoice number must be 35 characters";
    } else if (!isValidKeyboardInput(inputValue)) {
      errorMessage = "Invalid characters";
    }

    setIsErrorInvoice(errorMessage);
    //M10-12999
    setInvoiceNumber(inputValue);
    relockNumberVerification(); //M10-15262
  };

  function isValidDatePartsInISODate(dateStr: any) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T/.exec(dateStr);
    if (!match) return false;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);

    // Validate year range
    if (year < 1900 || year > 2100) return false;

    // Validate month
    if (month < 1 || month > 12) return false;

    // Validate day
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (day < 1 || day > daysInMonth[month - 1]) return false;

    return true;
  }

  const verifyStringType = function (input: string) {
    const date = new Date(input);
    const numberRegex = /^\d+$/;
    if (!isNaN(date.getTime()) && input?.trim() !== "" && !numberRegex.test(input)) {
      return moment(input).format("DD MMM, YYYY");
    } else {
      return input;
    }
  };
  const isRequestInProgress = useRef(false);

  const handleSubmitInvoice = useDebounceLeading(async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    try {
      //M10-15262
      if (isYetToProcess && (!isInvoiceNumberVerified || !isInvoiceDateVerified)) {
        toast({ description: "Please download the invoice and verify the Invoice Number and Invoice Date", variant: "destructive" });
        return;
      }
      //M10-15262
      // M10-13321
      if (invoiceDate < get120DaysAgoDate(invoiceData?.upload_type) && !hasPermissionInvoicePreDate) {
        toast({
          description:
            invoiceData?.upload_type === "INST"
              ? "Invoice date cannot be before Dec 1, 2025"
              : "Invoice date must be within the last 120 days",
          variant: "destructive",
        });
        return;
      }
      // M10-13321
      if (isNmvYetToProcess && !selectedCounter && !invoiceData?.counter_code && !invoiceData?.customer_name) {
        toast({
          description: allowCustomerNameEntry ? "Please select a Counter or enter Customer Name" : "Please select a Counter",
          variant: "destructive",
        });
        return;
      }
      //M10-11703
      if (isPoProportionate && !poReason?.trim()) {
        toast({
          description: "Reason is required",
          variant: "destructive",
        });
        return;
      }
      //M10-11703
      //M10-15818
      if (selectedPoRefOrderStatusId === 3) {
        toast({ description: "Order is fully invoiced", variant: "destructive" });
        return;
      }
      //M10-15818
      //M10-16162
      if (
        invoiceData?.module_code === 4 &&
        Number(tab) !== 1 &&
        ![6, 8].includes(invoiceData?.fileStatusHdr?.fuid_status_id) &&
        poRefNumber?.trim() &&
        !isPoRefMatched &&
        !poRefVerification.isVerified()
      ) {
        await poRefVerification.open(fetchPoRefList);
        return;
      }
      //M10-16162
      if (/[-_]\s?1$/.test(invoiceNumber)) {
        toast({
          description: "Invoice number cannot end with -1 or - 1",
          variant: "destructive",
        });
        return;
      }
      //M10-13053
      const isValidYesNo = (val:any) => val === "Yes" || val === "No";
      const invalidFields = [];
      if (!isValidYesNo(StampSeal)) {
        invalidFields.push("Stamp & Sign Status");
      }
      if (!isValidYesNo(category1)) {
        invalidFields.push("E-Inv");
      }
      if (invalidFields.length > 0 && invoiceData?.stamp_and_seal_required) {
        toast({
          description: `Please select Yes or No for: ${invalidFields.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
      //M10-13053
      //M10-12999
      if (Boolean(isErrorInvoice)) {
        toast({ description: isErrorInvoice, variant: "destructive" });
        return;
      }
      //M10-12999
      //M10-11021
      let vertifyResult = validateInvoiceNumber(invoiceNumber)
      if (!vertifyResult.valid) {
        toast({ description: vertifyResult?.error, variant: "destructive" });
        return;
      }
      //M10-11021
      // M10-16202: block until the E-Way Bill is accepted when any linked e-way-required order is not exempt.
      // Derived from the invoice's own orders so it works on every screen that uses this card.
      if (isEwayBillPending(invoiceData)) {
        toast({ description: EWAY_BILL_PENDING_MESSAGE, variant: "destructive" });
        onEwayBillPending?.();
        return;
      }
      if (invoiceDate == undefined || invoiceDate == "" || invoiceDate == null || invoiceDate == " ") {
        toast({ description: "Select Invoice Date before updating", variant: "destructive" });
        return;
      }
      //M10-11134
      if (new Date(invoiceDate) > new Date()) {
        toast({ description: "Invoice date cannot be in the future", variant: "destructive" });
        return;
      }
      //M10-11134
      if (!isValidDatePartsInISODate(invoiceDate)) {
        toast({ description: "Invoice Date is Incorrect.", variant: "destructive" });
        return;
      }

      // Mandatory only for KAM (module_code 4) invoices
      if (invoiceData?.module_code === 4 && !goodsDeliveredAvailability) {
        toast({ description: "Select Goods Delivered Date availability before updating", variant: "destructive" });
        return;
      }
      if (invoiceData?.module_code === 4 && !goodsDeliveredDate) {
        toast({ description: "Select Goods Delivered Date before updating", variant: "destructive" });
        return;
      }
      if (invoiceData?.module_code === 4 && new Date(goodsDeliveredDate) < new Date(invoiceDate)) {
        toast({ description: "Goods Delivered Date cannot be before Invoice Date", variant: "destructive" });
        return;
      }
      if (invoiceData?.module_code === 4 && new Date(goodsDeliveredDate) > new Date(getLiteralNowIso())) {
        toast({ description: "Goods Delivered Date cannot be in the future", variant: "destructive" });
        return;
      }

      // .every() on [] is vacuously true, so require at least one e-inv-required order before treating the invoice as exempt
      const einvRequiredOrders = invoiceData?.orderCodes?.filter((codes: any) => codes?.reference_order?.novu_metadata?.e_inv_required == "Y") ?? [];
      const isEinvExempt = einvRequiredOrders.length > 0 && einvRequiredOrders.every(
        (item: any) => item?.reference_order?.einv_exempt === true
      );
      if (!isEinvExempt && (
        (
          (!qrIRN?.trim() || qrIRN == null) &&
          data?.["E-Inv.Applicability"] == "Yes" &&
          !catTwoAndThree
        ) ||
        (
          category1 == 'Yes' &&
          (!qrIRN?.trim() || qrIRN == null)
        ))
      ) {
        toast({ description: "Add QR/IRN", variant: "destructive" });
        return;
      }

      //M10-11086
      // if (!IRNExempt) {
        if (
          (irnNumber?.trim() !== 'IMAGE FILE' &&
            data?.["E-Inv.Applicability"] == "Yes" &&
            !catTwoAndThree &&
            qrIRN?.trim() && // M10-13187
            qrIRN !== "QR" &&
            !isEinvExempt &&
            irnNumber?.trim().length !== 64) || (
            irnNumber?.trim() !== 'IMAGE FILE' &&
            category1 == 'Yes' &&
            qrIRN?.trim() && // M10-13187
            qrIRN !== "QR" &&
            !isEinvExempt &&
            irnNumber?.trim().length !== 64)
        ) {
          toast({ description: "IRN should be 64 digit", variant: "destructive" });
          return;
        }
      // }

      // if (((qrIRN == "IRN" || qrIRN == "BOTH") && irnNumber == " ") || irnNumber == "") {
      //   toast({ description: "Add IRN Number", variant: "destructive" });
      //   return;
      // }

      // if (gstStatus == "Yes" && !invoiceData?.counter_gst_number?.trim()) {
      //   toast({ description: "GST not validated", variant: "destructive" });
      //   return;
      // }
      // PUC (module_code 3) invoices skip GST / MCI / DL validations
      if (!isPucModule && gstStatus == "No" && invoiceData?.counter_gst_status) {
        toast({ description: "Validate GST Applicability as YES", variant: "destructive" }); //M10-11546
        return;
      }
      // if (dlValidated == "Yes" && !invoiceData?.counter_dl_number?.trim()) {
      //   toast({ description: "DL not validated", variant: "destructive" });
      //   return;
      // }
      // if (mciValid == "Yes" && !invoiceData?.counter_mci_number?.trim()) {
      //   toast({ description: "MCI not validated", variant: "destructive" });
      //   return;
      // }

      // if (data?.["Link Type"] == "DRDS" && mciValid == "Yes" && !invoiceData?.counter_mci_number?.trim()) {
      //   toast({ description: "MCI not validated", variant: "destructive" });
      //   return;
      // }
      if (!isPucModule && data?.["Link Type"] == "DRDS" && mciValid == "No" && invoiceData?.counter_mci_number?.trim()) {
        toast({ description: "Validate MCI Validated as YES", variant: "destructive" });
        return;
      }
      // if (data?.["Link Type"] == "DRDS" && mciValid == "No" && !invoiceData?.counter_mci_number?.trim()) {
      //   toast({ description: "MCI not validated", variant: "destructive" });
      //   return;
      // }
      // if (data?.["Link Type"] == "DRDS" && gstStatus == "Yes" && !invoiceData?.counter_gst_number?.trim()) {
      //   toast({ description: "GST not validated", variant: "destructive" });
      //   return;
      // }
      // if (
      //   (data?.["Link Type"] == "DRAPH" || data?.["Link Type"] == "INDPH-PH" || data?.["Link Type"] == "INDPH-HS") &&
      //   dlValidated == "Yes" &&
      //   (!invoiceData?.counter_dl_number?.trim() || invoiceData?.counter_dl_number?.trim() == undefined)
      // ) {
      //   toast({ description: "DL not validated", variant: "destructive" });
      //   return;
      // }
      // if (
      //   (data?.["Link Type"] == "DRAPH" || data?.["Link Type"] == "INDPH-PH" || data?.["Link Type"] == "INDPH-HS") &&
      //   dlValidated == "No" &&
      //   (!invoiceData?.counter_dl_number?.trim() || invoiceData?.counter_dl_number?.trim() == undefined)
      // ) {
      //   toast({ description: "DL not validated", variant: "destructive" });
      //   return;
      // }
      if (
        !isPucModule &&
        (data?.["Link Type"] == "DRAPH" || data?.["Link Type"] == "INDPH-PH" || data?.["Link Type"] == "INDPH-HS") &&
        dlValidated == "No" &&
        invoiceData?.counter_dl_number?.trim()
      ) {
        toast({ description: "Validate DL Present as YES", variant: "destructive" });
        return;
      }
      // if (
      //   (data?.["Link Type"] == "DRAPH" || data?.["Link Type"] == "INDPH-PH" || data?.["Link Type"] == "INDPH-HS") &&
      //   gstStatus == "Yes" &&
      //   (!invoiceData?.counter_gst_number?.trim() || invoiceData?.counter_gst_number?.trim() == undefined)
      // ) {
      //   toast({ description: "GST not validated", variant: "destructive" });
      //   return;
      // }

      setIsLoading(true);

      const currentDate = new Date(invoiceDate);
      const invoiceAccYear = getFinancialYear(currentDate);

      const payloadToValidateInV1 = {
        cmpstockistcode: invoiceData?.company_stockist_code,
        invoicenumber_searchcolumn: invoiceNumber || data?.["Invoice no."] || "",
        invoice_accyear: invoiceAccYear,
        company_code: invoiceData?.company_code,
      };
      //M10-11703
      if (!poReason.trim()) {
        const response = await getInvoiceChangeValidationV1(payloadToValidateInV1);
        if (response?.data?.success !== "success") {
          toast({ description: response?.data?.message, variant: "destructive" });
          setIsLoading(false);
          return;
        }
      }
      //M10-11703

      const payload = {
        data: {
          invoice_status: invoiceData?.fileStatusHdr?.fuid_status_shortcode,
          stockist_id: invoiceData?.stockist_id,
          stockist_invoice_attachment_id: invoiceData?.stockist_invoice_attachment_id,
          u_code: generateUniqueID(),
          new_invoice_date: invoiceDate,
          invoice_number: invoiceNumber ? invoiceNumber.trim() : data?.["Invoice no."] || "",
          ...(invoiceData?.module_code === 4 && {
            goods_delivered_date: goodsDeliveredDate,
            is_goods_delivered_date_available: goodsDeliveredAvailability === "Available",
          }),
          ...(qrIRN?.trim() !== "" && { e_inv_ref_type: qrIRN }),
          irn: qrIRN == "IRN" || qrIRN == "BOTH" ? irnNumber.trim() : "",
          gst_validated: gstStatus == "Yes" ? true : false,
          dl_validated: dlValidated == "Yes" ? true : false,
          mci_validated: mciValid == "Yes" ? true : false,
          counter_gst_number: invoiceData?.counter_gst_number || "",
          counter_dl_number: invoiceData?.counter_dl_number || "",
          counter_mci_number: invoiceData?.counter_mci_number || "",
          stamp_and_seal_validated: StampSeal == "Yes" ? true : false,
          is_category_1: category1 == "Yes" ? true : false,
          irn_exempt: false, //M10-11086
          //M10-15732
          poid_ref_number: poRefNumber?.trim() || null,
          is_poid_ref_matched: isPoRefMatched,
          stockist_po_attachment_id: selectedStockistPoAttachmentId,
          //M10-15732
          ...counterPayloadFields,
        },
        event_type: "INVOICE_ADD",
      };

      const payload2 = {
        data: {
          invoice_status: invoiceData?.fileStatusHdr?.fuid_status_shortcode,
          invoice_id: invoiceData?.invoice?.invoice_id,
          invoice_number: invoiceNumber?.trim(),
          u_code: invoiceData?.invoice?.u_code,
          new_invoice_date: invoiceData?.fileStatusHdr?.fuid_status_shortcode == "WFA" ? (tempDateChange ? invoiceDate : data?.["Invoice date"]) : invoiceDate, //M10-12296
          stockist_invoice_attachment_id: invoiceData?.stockist_invoice_attachment_id,
          stockist_id: invoiceData?.stockist_id,
          ...(invoiceData?.module_code === 4 && {
            goods_delivered_date: goodsDeliveredDate,
            is_goods_delivered_date_available: goodsDeliveredAvailability === "Available",
          }),
          ...(qrIRN?.trim() !== "" && { e_inv_ref_type: qrIRN }),
          irn: qrIRN == "IRN" || qrIRN == "BOTH" ? irnNumber.trim() : "",
          gst_validated: gstStatus == "Yes" ? true : false,
          dl_validated: dlValidated == "Yes" ? true : false,
          mci_validated: mciValid == "Yes" ? true : false,
          counter_gst_number: invoiceData?.counter_gst_number || "",
          counter_dl_number: invoiceData?.counter_dl_number || "",
          counter_mci_number: invoiceData?.counter_mci_number || "",
          header_edit: true,
          invoice_num_modified: invoiceData?.invoice?.invoice_number?.trim() !== invoiceNumber?.trim(),
          stamp_and_seal_validated: StampSeal == "Yes" ? true : false,
          is_category_1: category1 == "Yes" ? true : false,
          irn_exempt: false, //M10-11086
          //M10-15732
          poid_ref_number: poRefNumber?.trim() || null,
          is_poid_ref_matched: isPoRefMatched,
          stockist_po_attachment_id: selectedStockistPoAttachmentId,
          //M10-15732
          ...(invoiceData?.invoice?.invoiceProducts?.some((item: any) => item?.orderProductMappings?.some((m: any) => m.is_po_generated === false)) && { po_proportionate_exempt_invoice: poProportionate == "No" ? "Yes" : "No" }), //M10-11703
          counter_data_validation:
            invoiceData?.invoice?.dl_validated !== (dlValidated == "Yes" ? true : false) ||
            invoiceData?.invoice?.gst_validated !== (gstStatus == "Yes" ? true : false) ||
            invoiceData?.invoice?.mci_validated !== (mciValid == "Yes" ? true : false),
          ...counterPayloadFields,
        },
        primary_key: invoiceData?.invoice?.invoice_id,
        event_type: "INVOICE_EDIT",
      };

      if (invoiceData?.invoice == null) {
        const res = await uploadInvoice(payload, company_code);
        const response = await withEventCheck(
          () => {
            return getInvoiceDetails(invoiceData?.stockist_invoice_attachment_id as string, false, company_code);
          },
          res?.data?.data?.eventStreamCreated?.streamId,
          company_code  //M10-12758
        );
        dispatch(invoiceActions.setInvoiceDetails(response?.data?.data));
      } else {
        const res = await editInvoice(payload2, company_code);
        setPoProportionate(poProportionate == "No" ? "Yes" : "No");  //M10-11703
        const response = await withEventCheck(
          () => {
            return getInvoiceDetails(invoiceData?.stockist_invoice_attachment_id as string, false, company_code);
          },
          res?.data?.data?.invoice?.eventStreamCreated?.streamId,
          company_code  //M10-12758
        );
        if (Number(tab) === 1) {
          dispatch(invoiceActions?.setInvoiceApprovalData(response?.data?.data));
        } else {
          dispatch(invoiceActions?.setInvoiceDetails(response?.data?.data));
        }
      }
      //M10-11703
      if (poReason.trim()) {
        const remarkPayload = {
          data: {
            invoice_remarks_hdr: 22,
            invoice_id: invoiceData?.invoice?.invoice_id ? invoiceData?.invoice?.invoice_id : null,
            stockist_invoice_attachment_id: invoiceData?.stockist_invoice_attachment_id,
            freetxt_remarks: `Proportionate Exempt Reason: ${poReason}`,
            remarkby_type: idTokenDetails?.["custom:role"],
            is_private: false,
          },
          primary_key: invoiceData?.stockist_invoice_attachment_id,
          event_type: "STOCKIST_INVOICE_REMARK_ADD",
        };
        //M10-11536
        const res = await postInvoiceRemark(remarkPayload, invoiceData?.company_code);
        if (res?.status !== 200) throw new Error();
        setPoReason('')
        setIsPoProportionate(false)
      }
      //M10-11703
      setIsLoading(false);
      toast({ description: "Invoice header submitted", variant: "default" });
    } catch (error: any) {
      toast({ description: error?.response?.data?.message, variant: "destructive" });
      console.log(error, "error");
      setIsLoading(false);
    } finally {
      isRequestInProgress.current = false;
    }
  }, 1000);

  const handleCloseModal = (modalName: string) => {
    setModalStates((prev: any) => ({ ...prev, [modalName]: false }));
  };
  //M10-12020
  useEffect(() => {
    dispatch(invoiceActions.setTempInvoiceDate(data?.["Invoice date"]));
  }, [])
  //M10-12020
  // Goods Delivered Date: when marked Not Available, it always mirrors Invoice Date
  useEffect(() => {
    if (goodsDeliveredAvailability === "Not Available") {
      setGoodsDeliveredDate(invoiceDate);
    }
  }, [goodsDeliveredAvailability, invoiceDate]);
  //M10-12755
  const updateInvoiceDateApi = async () => {
    if (invoiceNumber?.trim() == isDuplicateNumber.trim()) {
      toast({ description: "Same Invoice Number Present", variant: "destructive" });
      return
    }

    function removeSpecialCharacters(str: string): string {
      return str.replace(/[^a-zA-Z0-9]/g, '');
    }

    const body = {
      data: {
        invoice_id: invoiceData?.invoice?.invoice_id,
        invoice_number: invoiceNumber?.trim(),
        Invoice_Number: invoiceNumber?.trim(),
        Invoice_Ucode: invoiceData?.invoice?.uid,
        InvoiceNumber_SearchColumn: removeSpecialCharacters(invoiceNumber),
        stockist_invoice_attachment_id: invoiceData?.stockist_invoice_attachment_id,
        stockist_id: invoiceData?.stockist_id,
        Stockist_Code: invoiceData?.company_stockist_code,
        new_invoice_date: invoiceDate,
      },
      primary_key: invoiceData?.invoice?.invoice_id,
      event_type: "INVOICE_NUMBER_RENAME"
    }
    try {
      //M10-13978
      const currentDate = new Date(invoiceDate);
      const invoiceAccYear = getFinancialYear(currentDate);

      const payloadToValidateInV1 = {
        cmpstockistcode: invoiceData?.company_stockist_code,
        invoicenumber_searchcolumn: invoiceNumber?.trim() || "",
        invoice_accyear: invoiceAccYear,
        company_code: invoiceData?.company_code,
      };

      const validationResponse = await getInvoiceChangeValidationV1(payloadToValidateInV1);
      if (validationResponse?.data?.success !== "success") {
        toast({ description: validationResponse?.data?.message, variant: "destructive" });
        return;
      }
    //M10-13978
      const data = await renameInvoiceNumber(body, company_code)
      if (data?.status !== 200) throw new Error();
    }
    catch (error: any) {
      toast({ description: error?.response?.data?.message, variant: "destructive" });
    }
  };
  //M10-12755

  return (
    <div className="-mt-10">
      <Modal isOpen={modalStates.reject} isHeader={false} onCloseModal={() => handleCloseModal("reject")} title="Reject Invoice">
        <InvoiceReject
          invoiceData={invoiceData}
          onCloseModal={() => handleCloseModal("reject")}
          onSuccess={() => isMountedRef.current && navigate("/transactions/invoice/0")} //M10-15110
        />
      </Modal>
      <Modal isOpen={modalStates.reassign} isHeader={false} onCloseModal={() => handleCloseModal("reassign")} title="Reassign Invoice">
        <InvoiceReassign invoiceData={invoiceData} onCloseModal={() => handleCloseModal("reassign")} />
      </Modal>

      {showHeader && (
        <header className={isCompleted ? "flex justify-between w-full" : "flex justify-between w-full px-5 py-5"}>
          <div className="flex flex-col gap-x-2">
            <div className="flex items-center gap-x-2">
              <h1 className="font-semibold text-[1.4rem] mr-4">
                <span className="text-gray-400 text-[1.4rem]">FUID#</span> {invoiceData?.fuid_no}
              </h1>
              <h1
                className={`font-semibold  flex items-center text-xs h-6 px-2 rounded-full`}
                style={{
                  backgroundColor: invoiceData?.fileStatusHdr?.color_code?.trim(),
                  color: invoiceData?.fileStatusHdr?.text_color_code?.trim(),
                }}
              >
                {status}
                <img className="w-3 h-3 ml-1" src={downArrow} alt="down-arrow" />
              </h1>
            </div>
          </div>
        </header>
      )}
      {showHeader && (
        <div className="flex justify-between">
          <div className="flex items-center ml-4 gap-x-2">
            <img className="w-6 h-6 ml-1" src={Contact} alt="person-img" />
            <span className="text-[#49484C] text-sm">Assigned to</span>
            <div className="w-10 h-10 bg-[#8051FF] flex items-center justify-center text-[#fff] rounded-full">
              {invoiceData?.InvoiceAssignment?.user_name?.slice(0, 2)}
            </div>
            <span className="text-[#49484C] text-sm">{invoiceData?.InvoiceAssignment?.user_name}</span>

            {invoiceData?.is_rejection_pending && invoiceData?.rejectionReviewAssignment?.user_name &&
          <>
            <img className="w-6 h-6 ml-10" src={Contact} alt="person-img" />
            <span className="text-[#49484C] text-sm">Escalated to</span>
            <div className="w-10 h-10 bg-[#8051FF] flex items-center justify-center text-[#fff] rounded-full">
              {invoiceData?.rejectionReviewAssignment?.user_name?.slice(0, 2)}
            </div>
            <span className="text-[#49484C] text-sm">{invoiceData?.rejectionReviewAssignment?.user_name}</span>
          </>}
          </div>
          <div className="flex items-center gap-x-2">
            {
              <div className="flex items-center p-2 rounded-md bg-[#f1f1f1]">
                <Button
                  className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1] border-gray-500 rounded-r-none border-r rounded-l-md h-5 hover:text-gray-600 focus:outline-none focus:ring-0"
                  onClick={() => {
                    if (!invoiceData?.file_path) {
                      toast({ description: "Invoice not found", variant: "destructive" });
                      return;
                    }
                    downloadInvoice(invoiceData?.file_path, company_code).then((res: any) => {
                      if (res?.data?.success && res?.data?.data) {
                        window.open(res?.data?.data, "_blank");
                        setIsInvoiceDownloaded(true); //M10-15262
                      } else {
                        console.error("Failed to download the invoice.");
                      }
                    });
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M7 8l5 5m0 0l5-5m-5 5V3" />
                  </svg>
                  <span>Download Invoice</span>
                </Button>
                {invoiceData?.einv_attachment_path && (
                  <Button
                    className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1] border-gray-500 rounded-r-none border-r rounded-l-md h-5 hover:text-gray-600 focus:outline-none focus:ring-0"
                    onClick={() => {
                      if (!invoiceData?.einv_attachment_path) {
                        toast({ description: "Invoice not found", variant: "destructive" });
                        return;
                      }
                      downloadInvoice(invoiceData?.einv_attachment_path, company_code).then((res: any) => {
                        if (res?.data?.success && res?.data?.data) {
                          window.open(res?.data?.data, "_blank");
                        } else {
                          console.error("Failed to download the invoice.");
                        }
                      });
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M7 8l5 5m0 0l5-5m-5 5V3" />
                    </svg>
                    <span>Download E-Invoice</span>
                  </Button>
                )}
                {/* M10-15105 */}
                <Button
                  className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1] border-gray-500 border-r rounded-none h-5 hover:text-gray-600 focus:outline-none focus:ring-0"
                  onClick={() => {
                    if (!invoiceData?.textract_file_path) {
                      toast({ description: "MV Lens Output not found", variant: "destructive" });
                      return;
                    }
                    downloadInvoice(invoiceData?.textract_file_path, company_code).then((res: any) => {
                      if (res?.data?.success && res?.data?.data) {
                        window.open(res?.data?.data, "_blank");
                      } else {
                        console.error("Failed to download the MV Lens Output.");
                      }
                    });
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M7 8l5 5m0 0l5-5m-5 5V3" />
                  </svg>
                  <span>MV Lens Output</span>
                </Button>
                {/* M10-15105 */}
                <Button onClick={() => setEscalateToMasterOpen(true)} className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1]  border-none rounded-none h-5 hover:text-gray-600 focus:outline-none focus:ring-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                  <span>Escalate to master</span>
                </Button>
                <Button className="flex items-center px-4 space-x-2 font-normal bg-[#f1f1f1] border-gray-500 border-l rounded-l-none rounded-r-md h-5 hover:text-gray-600 focus:outline-none focus:ring-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 0m12.728 0L12 12m0 0l-6.364-6.364" />
                  </svg>
                  <span>Report an issue</span>
                </Button>
              </div>
            }
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center items-center h-[20vh]">
          <Spinner />
        </div>
      ) : (
        <div
          className={`overflow-hidden items-center transition-all duration-500 px-5 ease-in-out`}
          style={{ transitionProperty: "max-height, opacity" }}
        >
          <div className="flex justify-between items-center pb-5 mt-10 border-b-2 border-gray-200">
            <div className="flex items-center gap-x-4">
              <p className="text-xl font-semibold">Header Information</p>
              {/* {!isExpanded && (
                <>
                  {invoiceData?.orderCodes?.length > 0 && (
                    <p className="text-sm text-gray-500 border border-gray-300 rounded-lg p-1 px-2">
                      Order Code: {invoiceData?.orderCodes?.map((code: any) => code?.order_code).join(", ")}
                    </p>
                  )}
                </>
              )} */}
            </div>
            <div className="p-2 bg-[#F1F1F1] border-2 border-gray-200 rounded-md cursor-pointer" onClick={handleToggle}>
              <img className={`transition-transform  duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
            </div>
          </div>

          <div
            className={`transition-all items-center duration-500 ease-in-out ${isExpanded ? "opacity-100 max-h-[1000px]" : "max-h-0 opacity-0"}`}
            style={{ transitionProperty: "max-height, opacity" }}
          >
            <div className={"grid grid-cols-1 w-[100%] py-5 mt-5 xl:grid-cols-3 gap-y-5"}>
              {!!data &&
                Object.keys(data)?.length > 0 &&
                Object.entries(data)?.map(
                  ([key, value]) =>
                    typeof value === "string" &&
                    value !== "" && (
                      <div key={key?.toString()} className={"grid grid-cols-2 -space-x-10 -space-y-0 text-sm xl:grid-cols-2 items-center"}>
                        <p
                          className={`text-[#49484C] place-self-center mr-auto whitespace-break-spaces text-sm leading-[0.02rem] not-italic ${key === "Pharmacy" || key === "Counter" ? "font-bold" : "font-normal"
                            }`}
                        >
                          {key === "Counter" && isCustomerNameEntry ? "Customer Name" : switchString(key)} :
                        </p>
                        {key == "GST Applicability" && ( //M10-11546
                          <div className="w-[60%]">
                            {invoiceData?.counter_gst_status ?
                              <Toggle
                                disabled={
                                  invoiceData?.fileStatusHdr?.fuid_status_id === 8 || invoiceData?.fileStatusHdr?.fuid_status_id === 6
                                  // !data?.["GST No."]?.trim()?.length
                                }
                                checked={gstStatus == "Yes"}
                                onChange={(e: any) => {
                                  setGstStatus(e === true ? "Yes" : "No");
                                }}
                              /> : <p>No</p>
                            }
                          </div>
                        )}
                        {key == "Stamp & Sign Status" && (
                          <div className="w-[60%]">
                            {/* M10-13053 */}
                            <Autocomplete
                              inputClassName="relative"
                              placeholder="Select"
                              defaultValue={StampSeal}
                              suggestionList={[
                                { key: "Select", value: "" },
                                { key: "Yes", value: "Yes" },
                                { key: "No", value: "No" },
                              ]}
                              disabled={
                                isPucModule ||
                                invoiceData?.fileStatusHdr?.fuid_status_id === 8 ||
                                invoiceData?.fileStatusHdr?.fuid_status_id === 6
                              }
                              onSelectionChange={(e: any) => {
                                if (e === null) {
                                  setStampSeal(null);
                                  return;
                                }
                                setStampSeal(e.value);
                              }}
                              displayKey="key"
                            />
                            {/* M10-13053 */}
                          </div>
                        )}
                        {key == "E-Inv" && (
                          <div className="w-[60%]">
                            {/* M10-13053 */}
                            <Autocomplete
                              inputClassName="relative"
                              placeholder="Select"
                              defaultValue={category1}
                              suggestionList={[
                                { key: "Select", value: "" },
                                { key: "Yes", value: "Yes" },
                                { key: "No", value: "No" },
                              ]}
                              disabled={
                                isPucModule ||
                                invoiceData?.fileStatusHdr?.fuid_status_id === 8 ||
                                invoiceData?.fileStatusHdr?.fuid_status_id === 6
                              }
                              onSelectionChange={(e: any) => {
                                if (e === null) {
                                  setCategory1(null);
                                  return;
                                }

                                setCategory1(e.value);

                                if (data?.["E-Inv.Applicability"] === "No" && e.value === "No") {
                                  setQrIRN(null);
                                  setIrnNumber("");
                                  // setIRNValidation(false)
                                }
                              }}
                              displayKey="key"
                            />
                            {/* M10-13053 */}
                          </div>
                        )}
                        {key == "PO Ref No" && (
                          <div className="w-[60%]">
                            {/* M10-15732 */}
                            {Number(tab) === 1 ? (
                              <p className="text-[#1B1A1F] mr-auto">{poRefNumber ? poRefNumber : '-'}</p>
                            ) : (
                              <Autocomplete
                                inputClassName="relative"
                                placeholder="Select or type PO Ref No"
                                defaultValue={poRefNumber}
                                suggestionList={[]}
                                asyncListFunction={fetchPoRefList}  // M10-15867
                                isAsyncQuery={false}
                                displayKey="poid_ref_number"
                                uniqueKey="stockist_po_attachment_id"
                                allowCustomValue
                                customFieldName="poid_ref_number"
                                disabled={
                                  invoiceData?.fileStatusHdr?.fuid_status_id === 8 ||
                                  invoiceData?.fileStatusHdr?.fuid_status_id === 6
                                }
                                onSelectionChange={handlePoRefChange}
                              />
                            )}
                            {/* M10-15732 */}
                          </div>
                        )}
                        {key == "DL Present" && (
                          <div className="w-[60%]">
                            <Toggle
                              disabled={
                                invoiceData?.fileStatusHdr?.fuid_status_id === 8 || invoiceData?.fileStatusHdr?.fuid_status_id === 6
                                // !data?.["DL no."]?.trim()?.length
                              }
                              checked={dlValidated == "Yes"}
                              onChange={(e: any) => {
                                setdlValidated(e === true ? "Yes" : "No");
                              }}
                            />
                          </div>
                        )}

                        {key == "QR/IRN" && (
                          <div className="w-[60%]">
                            {/* "E-Inv Exempt" is "-" when there are no order codes to derive exemption from;
                                only hide the field when exemption is explicitly known ("Yes") */}
                            {data?.["E-Inv Exempt"] !== 'Yes' &&
                              <Autocomplete
                                inputClassName="relative"
                                placeholder={key}
                                defaultValue={qrIRN}
                                suggestionList={[
                                  { key: "Select", value: "" },
                                  { key: "QR", value: "QR" },
                                  { key: "IRN", value: "IRN" },
                                  { key: "BOTH", value: "BOTH" },
                                ]}
                                disabled={
                                  category1 !== "Yes" && (
                                    data?.["E-Inv.Applicability"] === "No" ||
                                    [6, 8].includes(invoiceData?.fileStatusHdr?.fuid_status_id)
                                  )
                                }
                                onSelectionChange={(e: any) => {
                                  if (e === null) {
                                    setQrIRN(null);
                                    return;
                                  }
                                  setQrIRN(e.value);
                                  //M10-11086
                                  // if (e.value === 'IRN' || e.value === 'BOTH') {
                                  //   setIRNValidation(true)
                                  // } else {
                                  //   setIRNValidation(false)
                                  // }
                                  //M10-11086
                                }}
                                displayKey="key"
                              />}
                            {/* M10-11086 */}
                            {/*
                            {IRNValidation &&
                              <span className="m-2 inline-flex items-center gap-2">
                                IRN Exemption :  <Toggle
                                  checked={IRNExempt}
                                  onChange={(e: any) => {
                                    setIRNExempt(e === true);
                                  }}
                                />
                              </span>
                            }
                              */}
                            {/* M10-11086 */}
                          </div>
                        )}
                        {(qrIRN === "IRN" || qrIRN === "BOTH") && key === "Irn Number" && (
                          <div className="w-full mt-2">
                            <Input
                              enableArrows={false}
                              showIcon={false}
                              placeholder="IRN Number"
                              type="text"
                              value={irnNumber}
                              disabled={
                                (data?.["E-Inv.Applicability"] == "No" ||
                                  invoiceData?.fileStatusHdr?.fuid_status_id === 8 ||
                                  invoiceData?.fileStatusHdr?.fuid_status_id === 6) &&
                                (category1 == 'Yes' ? false : true)
                              }
                              onChange={(e: any) => setIrnNumber(e.target.value)}
                              className="w-full py-1 text-left border border-gray-300 rounded-lg"
                            />
                          </div>
                        )}
                        {key === "Invoice no." && (
                          // M10-12755
                          <div className="w-full flex items-center gap-4">
                            <div className="flex-1">
                              <Input
                                disabled={(!isEditingInvoiceNumber && [5, 8].includes(invoiceData?.fileStatusHdr?.fuid_status_id)) || (isYetToProcess && !isInvoiceDownloaded)}
                                enableArrows={false}
                                showIcon={false}
                                placeholder={key}
                                type="text"
                                value={invoiceNumber}
                                onChange={handleInputChange}
                                className="w-full py-1 text-left border border-gray-300 rounded-lg"
                                isError={Boolean(isErrorInvoice)}  //M10-12999
                                errorMessage={isErrorInvoice}  //M10-12999
                              />
                            </div>

                            {[5, 8].includes(invoiceData?.fileStatusHdr?.fuid_status_id) && (
                              <div className="flex items-center gap-2">
                                {/* ✏️ Pencil icon */}
                                {!isEditingInvoiceNumber && !isRenamePermissionLoading && invoiceNoRenamePermission && (
                                  <button
                                    type="button"
                                    className="text-gray-500 hover:text-blue-600"
                                    onClick={() => {
                                      setDuplicateNumber(invoiceNumber)
                                      setIsEditingDate(true)
                                    }}
                                    title="Edit Invoice Number"
                                  >
                                    ✏️
                                  </button>
                                )}

                                {/* 💾 Save button */}
                                {isEditingInvoiceNumber && (
                                  <button
                                    type="button"
                                    className="text-green-600 hover:text-green-800"
                                    onClick={async () => {
                                      await updateInvoiceDateApi();
                                      setIsEditingDate(false);
                                    }}
                                    title="Save"
                                  >
                                    ✔
                                  </button>
                                )}
                                {isEditingInvoiceNumber && (<button
                                  type="button"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => {
                                    setIsEditingDate(false);
                                    setInvoiceNumber(isDuplicateNumber)
                                  }}
                                  title="Cancel"
                                >
                                  ✖
                                </button>)}
                              </div>
                            )}
                            {/* M10-12755 */}
                            {/* M10-15262 */}
                            {isYetToProcess && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={!isInvoiceDownloaded || !invoiceNumber?.trim()}
                                  onClick={() => setIsInvoiceNumberVerified(true)}
                                  title={!invoiceNumber?.trim() ? "Nothing to verify" : isInvoiceNumberVerified ? "Verified" : "Click to verify"}
                                  className={!isInvoiceDownloaded || !invoiceNumber?.trim() ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                                >
                                  {isInvoiceNumberVerified ? (
                                    <CheckCircle size={20} color="green" />
                                  ) : (
                                    <Circle size={20} className="text-gray-300" />
                                  )}
                                </button>
                              </div>
                            )}
                            {/* M10-15262 */}
                          </div>
                        )}

                        {/* M10-12020 */}
                        {key === "Invoice date" && (
                          <div className="w-full flex items-center gap-4">
                            <div className="flex-1">
                              <DatePicker
                                disabled={invoiceData?.fileStatusHdr?.fuid_status_id === 8 || (isYetToProcess && !isInvoiceDownloaded)}
                                placeholder="dd-mm-yyyy"
                                value={invoiceDate ? invoiceDate.split("T")[0] : value}
                                max={moment().format("YYYY-MM-DD")} //M10-14313
                                min={!hasPermissionInvoicePreDate ? get120DaysAgoDate(invoiceData?.upload_type) : undefined} // M10-13321
                                onChange={(iso) => {
                                  dispatch(invoiceActions.setTempInvoiceDate(iso));
                                  setInvoiceDate(iso);
                                  relockDateVerification(); //M10-15262
                                }}
                              />
                            </div>

                            {[6].includes(invoiceData?.fileStatusHdr?.fuid_status_id) && (
                              <div className="flex items-center gap-2">
                                <Toggle
                                  checked={tempDateChange}
                                  onChange={(e: any) => settempDateChange(e === true)}
                                  module={
                                    invoiceData?.invoice
                                      ? fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module
                                      : fineGrainedPermissionsKeys.INVOICE.SUBMITINVOICE.module
                                  }
                                  action={
                                    invoiceData?.invoice
                                      ? fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action
                                      : fineGrainedPermissionsKeys.INVOICE.SUBMITINVOICE.action
                                  }
                                />
                              </div>
                            )}
                            {/* M10-15262 */}
                            {isYetToProcess && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={!isInvoiceDownloaded}
                                  onClick={() => setIsInvoiceDateVerified(true)}
                                  title={isInvoiceDateVerified ? "Verified" : "Click to verify"}
                                  className={!isInvoiceDownloaded ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                                >
                                  {isInvoiceDateVerified ? (
                                    <CheckCircle size={20} color="green" />
                                  ) : (
                                    <Circle size={20} className="text-gray-300" />
                                  )}
                                </button>
                              </div>
                            )}
                            {/* M10-15262 */}
                          </div>
                        )}
                        {/* M10-12020 */}
                        {key === "Goods Delivered Date*" && (
                          <div className="w-full flex items-center gap-4">
                            <div className="w-[40%]">
                              <Autocomplete
                                inputClassName="relative"
                                placeholder="Select"
                                defaultValue={goodsDeliveredAvailability}
                                suggestionList={[
                                  { key: "Available", value: "Available" },
                                  { key: "Not Available", value: "Not Available" },
                                ]}
                                disabled={invoiceData?.fileStatusHdr?.fuid_status_id === 8}
                                onSelectionChange={(e: any) => {
                                  if (e === null) return;
                                  setGoodsDeliveredAvailability(e.value);
                                }}
                                displayKey="key"
                              />
                            </div>
                            <div className="flex-1">
                              {goodsDeliveredAvailability === "Available" && (
                                // Defaults to Invoice Date but stays freely editable — user can pick any date/time >= Invoice Date
                                <DateTimeInput
                                  label=""
                                  value={goodsDeliveredDate || value}
                                  min={invoiceDate || undefined}
                                  max={getLiteralNowIso()}
                                  onChange={(iso) => setGoodsDeliveredDate(iso)}
                                />
                              )}
                              {goodsDeliveredAvailability === "Not Available" && (
                                <p className="text-[#1B1A1F]">{verifyStringType(invoiceDate || value)}</p>
                              )}
                            </div>
                          </div>
                        )}
                        {key == "MCI Validated" && (
                          <div className="w-[60%]">
                            <Toggle
                              disabled={
                                isPucModule ||
                                invoiceData?.fileStatusHdr?.fuid_status_id === 8 || invoiceData?.fileStatusHdr?.fuid_status_id === 6
                                // !data?.["MCI no."]?.trim()?.length
                              }
                              checked={mciValid == "Yes"}
                              onChange={(e: any) => {
                                setMciValid(e === true ? "Yes" : "No");
                              }}
                            />
                          </div>
                        )}
                        {key === "Order Code" &&
                          value?.split(",").map((orderCode, index) => {
                            const trimmedCode = orderCode.trim();
                            return (
                              <span key={index}>
                                <a
                                  className="text-gray-900 hover:text-[#6b40d6] w-fit"
                                  href={`/transactions/orders/info/${trimmedCode}/1`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => {
                                    window.localStorage.setItem("counterCompanyLocationId", invoiceData?.counter_company_location_id);
                                    window.localStorage.setItem("company_code", invoiceData?.company_code);
                                  }}
                                >
                                  {trimmedCode}
                                </a>
                                {index < value.split(",").length - 1 && ", "}
                              </span>
                            );
                          })}
                        {key == "Counter code" && !isNmvYetToProcess && (
                          <a
                            className="text-gray-900 hover:text-[#6b40d6]"
                            href={`/masters/counters/details/?counter_code=${value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              dispatch(counterActions.setClickType("row"));
                              dispatch(counterActions.setSelectedCounterData({}));
                              window.localStorage.setItem("counterOrderId", value);
                              window.localStorage.setItem("counterCompanyLocationId", invoiceData?.counter_company_location_id);
                            }}
                          >
                            {value}
                          </a>
                        )}
                        {isNmvYetToProcess && (key === "Counter" || key === "Counter code") && (
                          <div className="flex items-center gap-2 mr-auto">
                            <p className="text-[#1B1A1F]">
                              {key === "Counter"
                                ? (selectedCounter?.isCustomerEntry ? selectedCounter.customer_name : (selectedCounter?.counter_name || value))
                                : (selectedCounter?.isCustomerEntry ? "-" : (selectedCounter?.counter_code || value))}
                            </p>
                            {key === "Counter" && (!invoiceData?.counter_code || invoiceData?.upload_type !== "INST" || hasPermissionEditCounter) && (
                              <button type="button" onClick={openCounterModal}>
                                <img src={editPen} alt="edit" className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                        {isNmvYetToProcess && selectedCounter && (key === "Location Name" || key === "Location Code") && (
                          <div className="mr-auto">
                            <p className="text-[#1B1A1F] mr-auto">
                              {key === "Location Name" ? (selectedCounter?.location_name || "-") : (selectedCounter?.location_code || "-")}
                            </p>
                          </div>
                        )}
                        {key !== "QR/IRN" &&
                          key !== "DL Present" &&
                          key !== "GST Applicability" &&  //M10-11546
                          key !== "MCI Validated" &&
                          key !== "E-Inv" &&
                          key !== "Stamp & Sign Status" &&
                          key !== "Irn Number" &&
                          key !== "Invoice no." &&
                          key !== "Invoice date" &&
                          key !== "Goods Delivered Date*" &&
                          key !== "Order Code" &&
                          key !== "Po Proportionate Exempt" &&  //M10-11703
                          key !== "PO Ref No" &&  //M10-15732
                          key !== "Counter code" &&
                          !(isNmvYetToProcess && key === "Counter") &&
                          !(isNmvYetToProcess && selectedCounter && (key === "Location Name" || key === "Location Code")) && (
                            <div className="mr-auto">
                              {checkDataType && <p className="text-[#1B1A1F] mr-auto">{typeof value === "string" ? verifyStringType(value) : ""}</p>}
                              {!checkDataType && <p className="text-[#1B1A1F] mr-auto">{value}</p>}
                            </div>
                          )}
                        {/* M10-11703 */}
                        {key == "Po Proportionate Exempt" && (
                          <>
                            {
                              invoiceData?.invoice?.invoiceProducts?.some((item: any) =>
                                item?.orderProductMappings?.some((m: any) => m.is_po_generated === false)
                              ) ? (
                                <div className="w-[60%]">
                                  <Toggle
                                    disabled={invoiceData?.fileStatusHdr?.fuid_status_id !== 8}
                                    checked={poProportionate === "Yes"}
                                    onChange={() => {
                                      setIsPoProportionate(true);
                                    }}
                                  />
                                </div>
                              ) : (
                                <div>-</div>
                              )
                            }
                          </>
                        )}
                        {/* M10-11703 */}
                      </div>
                    )
                )}
            </div>
          </div>
          <div className="flex items-center justify-end mb-10 gap-x-4">
            {!invoiceData?.invoice && <p className="text-gray-700">Please submit the invoice header first!</p>}

            {hasPermissionReject && [1, 6, 24, 22].includes(invoiceData?.fileStatusHdr?.fuid_status_id) && isMapped && !isFuidRejectionReview && (
              <Button
                onClick={() => setModalStates((prev: any) => ({ ...prev, reject: true }))}
                className="h-9 px-6 text-white font-normal bg-orange-600 shadow-sm transition hover:bg-red-700"
              >
                Reject Invoice
              </Button>
            )}

            {/* M10-10941 Only (Yet To Process,Waiting For Approval,On Hold,Others,PTR)*/}
            {[1, 6, 4, 22, 23, 24].includes(invoiceData?.fileStatusHdr?.fuid_status_id) && isMapped && !isFuidRejectionReview && ( //M10-11117
              <Button
                className="h-9 px-6 text-white font-normal bg-[#8051FF] shadow-sm transition hover:bg-[#6b40d6]"
                type="submit"
                onClick={handleSubmitInvoice}
                isLoading={isLoading}
                disabled={isRequestInProgress.current}
                module={
                  invoiceData?.invoice
                    ? fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module
                    : fineGrainedPermissionsKeys.INVOICE.SUBMITINVOICE.module
                }
                action={
                  invoiceData?.invoice
                    ? fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action
                    : fineGrainedPermissionsKeys.INVOICE.SUBMITINVOICE.action
                }
              >
                {invoiceData?.invoice ? "Update Invoice Header" : "Submit Invoice Header"}
              </Button>
            )}
          </div>
        </div>
      )
      }
      <Modal isOpen={escalateToMasterOpen} isHeader={false} onCloseModal={() => setEscalateToMasterOpen(false)}>
        <EscalateToMaster
          onSuccess={() => {
            // navigate("/transactions/invoice/0");
          }}
          onCloseModal={() => setEscalateToMasterOpen(false)}
        />
      </Modal>
      <SelectCounterModal
        isOpen={isCounterModalOpen}
        onClose={closeCounterModal}
        counterList={counterList}
        tempSelectedCounter={tempSelectedCounter}
        setTempSelectedCounter={setTempSelectedCounter}
        onConfirm={confirmCounterSelection}
        allowCustomerNameEntry={allowCustomerNameEntry}
      />
      {/* M10-11703 */}
      <Modal isOpen={isPoProportionate} isHeader={false} onCloseModal={() => setIsPoProportionate(false)}>
        <div className="flex flex-col gap-y-2 mb-4 w-[340px] max-h-[318px] mx-auto px-6 bg-white p-4 rounded-xl">
          <div className="flex justify-between items-center">
            <p className="text-normal py-1 font-semibold text-[#49484C]">Proportionate Exempt for PO</p>
            <Button className="bg-transparent -mr-3" onClick={() => setIsPoProportionate(false)}>
              <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
            </Button>
          </div>
          <div>
            <p className="text-sm mb-2 mt-2 font-normal text-[#666666]">Reason</p>
            <Input
              type="text"
              placeholder="Enter Reason"
              value={poReason}
              onChange={(e) => {
                setPoReason(e.target.value)
              }}
              className="w-full border py-4 border-[#D0D0D0] rounded-md p-2 h-8"
            />
          </div>

          <Button loaderSize="small" isLoading={isLoading} className="text-white bg-[#8051FF] mt-2" onClick={handleSubmitInvoice}>
            Save Changes
          </Button>
        </div>
      </Modal>
      {/* M10-11703 */}
      {/* M10-16162 */}
      <VerifyPoRefModal
        isOpen={poRefVerification.isOpen}
        poRefNumber={poRefNumber}
        matches={poRefVerification.matches}
        onClose={poRefVerification.close}
        onSelectMatch={(po) => {
          handlePoRefChange(po);
          poRefVerification.close();
          toast({ description: `PO Ref No set to ${po?.poid_ref_number}. Please submit the invoice header again.` });
        }}
        onConfirm={() => {
          poRefVerification.markVerified();
          handleSubmitInvoice();
        }}
      />
      {/* M10-16162 */}
    </div >
  );
};

export default InvoiceDetailCard;
